<template>
  <div class="flex items-center xs:gap-x-5 md:gap-[30px]">
    <div
      class="group flex h-auto items-center justify-center rounded-md transition duration-100 ease-linear hover:text-orange-500 xs:flex xs:text-gray-600 lg:hidden"
      data-testid="search-mobile-button"
      @click="openSearchFlyout"
    >
      <IconSearch
        class="size-5 shrink-0 [&path]:!fill-gray-600 [&path]:!stroke-gray-600"
      />
    </div>

    <SFTooltip>
      <SFLocalizedLink
        :to="accountLink"
        raw
        class="group flex h-auto items-center justify-center rounded-md text-white transition duration-100 ease-linear hover:text-orange-500 xs:text-gray-600 lg:text-white"
        data-testid="header-user-button"
        :aria-label="$t('tooltip.my_account.title')"
        @click="handleAccountClick"
      >
        <IconUser
          class="shrink-0 transition-colors duration-100 xs:size-5 md:size-6"
        />
        <div
          v-if="isAuthUser"
          class="absolute bottom-0 right-[-5px] box-content flex size-4 items-center justify-center rounded-full bg-orange-500 pt-px"
        >
          <span class="text-sm text-white">
            <IconCheck class="size-3" />
          </span>
        </div>
      </SFLocalizedLink>

      <template #content>
        <SFHeaderUserAcount :is-logged-in="isAuthUser" />
      </template>
    </SFTooltip>

    <SFTooltip>
      <SFLocalizedLink
        :to="routeList.wishlist"
        raw
        class="group flex h-auto items-center justify-center rounded-md transition duration-100 ease-linear hover:text-orange-500 xs:text-gray-600 lg:text-white"
        data-testid="wishlist-link"
        :aria-label="
          $t('navigation.wishlist', mounted ? wishlistCount || 0 : 0)
        "
      >
        <IconHeartBold
          class="shrink-0 transition-colors duration-100 xs:size-5 md:size-6"
        />

        <HeaderBadge :count="wishlistCount || 0" />
      </SFLocalizedLink>

      <template #content>
        <SFHeaderWishlist />
      </template>
    </SFTooltip>

    <SFTooltip v-model:show="activeBasketState" @click-outside="closeModalCard">
      <SFLocalizedLink
        :to="routeList.cart"
        raw
        class="group flex h-auto items-center justify-center rounded-md transition duration-100 ease-linear hover:text-orange-500 xs:text-gray-600 lg:text-white"
        data-testid="basket-link"
        :aria-label="
          $t('basket_page.a11y.title', mounted ? basketCount || 0 : 0)
        "
        @click="closeModalCard"
      >
        <IconBasket
          class="shrink-0 transition-colors duration-100 xs:size-5 md:size-6"
        />

        <HeaderBadge :count="basketCount || 0" />
      </SFLocalizedLink>

      <template #content>
        <SFHeaderBasket />
      </template>
    </SFTooltip>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { useMounted } from '@vueuse/core'
import SFHeaderWishlist from './wishlist/SFHeaderWishlist.vue'
import SFHeaderBasket from './basket/SFHeaderBasket.vue'
import HeaderBadge from './HeaderBadge.vue'
import SFHeaderUserAcount from './user/SFHeaderUserAcount.vue'
import { routeList } from '~/utils/route'
import { useRoute } from '#app/composables/router'
import { useBasket, useUser, useWishlist } from '#storefront/composables'
import { useRouteHelpers } from '~/composables'
import SFLocalizedLink from '~/components/SFLocalizedLink.vue'
import SFTooltip from '~/components/ui/SFTooltip.vue'
import { useDefaultBreakpoints, useSlideIn } from '~/modules/ui/runtime'
import { usePageState } from '~/composables/usePageState'
import { useBasketFlyout } from '~/composables/basket/useBasketFlyout'
import { useGuestCookie } from '~/composables/useGuestCookie'

const { toggle: openSearchFlyout } = useSlideIn('SearchFlyout')
const { getLocalizedRoute } = useRouteHelpers()
const route = useRoute()
const mounted = useMounted()
const hasMountedOnce = ref(false)
const { toggle, isOpen } = useSlideIn('mobileBasketInfo')
const { user } = await useUser()
const { count: basketCount } = useBasket()
const { count: wishlistCount } = useWishlist()
const { guestUserActiveCookie: setGuestUserActive } = useGuestCookie()
const { greaterOrEqual } = useDefaultBreakpoints()
const isDesktop = greaterOrEqual('lg')

const isAuthUser = computed(
  () =>
    !!(
      user.value &&
      user.value.status?.isActive &&
      !user.value.status?.isGuestCustomer
    ),
)

const { setPageState } = usePageState()

const accountLink = computed(() => {
  if (isAuthUser.value && mounted.value) {
    return routeList.account
  }
  return route.path !== getLocalizedRoute(routeList.signin)
    ? routeList.signin
    : route.fullPath
})

const handleAccountClick = () => {
  setPageState('login_origin', 'header')
  setGuestUserActive.value = false
}

const { activeBasketState, closeModalCard } = useBasketFlyout()

watch(basketCount, async (newCount, oldCount) => {
  const isCartRoute = route.meta.SnPageType === 'cart'
  const isAuthRoute = route.path.includes('/signin')
  if (!hasMountedOnce.value && newCount === 0) {
    hasMountedOnce.value = true
    return
  }

  if (mounted.value && newCount !== oldCount) {
    await nextTick()
    if (isDesktop.value && !isOpen.value && !isCartRoute && !isAuthRoute) {
      activeBasketState.value = true
      toggle()
    }
  }
})
</script>
